// src/contexts/AccessibilityContext.tsx
import { logger } from '../utils/logger';
// Context for global accessibility state management

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  AccessibilityPreferences,
  ColorblindTheme,
  defaultAccessibilityPreferences,
  getAccessibilityPreferences,
  updateAccessibilityPreferences,
} from '../services/accessibilityService';
import { themeService } from '../services/themeService';

interface AccessibilityContextType {
  preferences: AccessibilityPreferences;
  loading: boolean;
  updatePreference: (key: keyof AccessibilityPreferences, value: boolean) => Promise<void>;
  refreshPreferences: () => Promise<void>;
}

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within AccessibilityProvider');
  }
  return context;
};

interface AccessibilityProviderProps {
  children: ReactNode;
}

export const AccessibilityProvider: React.FC<AccessibilityProviderProps> = ({ children }) => {
  const [preferences, setPreferences] = useState<AccessibilityPreferences>(defaultAccessibilityPreferences);
  const [loading, setLoading] = useState(true);

  // Load preferences on mount
  useEffect(() => {
    loadPreferences();
  }, []);

  // Apply CSS classes and theme changes based on preferences
  useEffect(() => {
    const root = document.documentElement;

    // Reduced motion
    if (preferences.reducedMotion) {
      root.classList.add('reduced-motion');
    } else {
      root.classList.remove('reduced-motion');
    }

    // Enhanced focus
    if (preferences.enhancedFocus) {
      root.classList.add('enhanced-focus');
    } else {
      root.classList.remove('enhanced-focus');
    }

    // High contrast
    if (preferences.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }

    // Larger touch targets
    if (preferences.largerTouchTargets) {
      root.classList.add('larger-touch-targets');
    } else {
      root.classList.remove('larger-touch-targets');
    }

    // Simplified navigation
    if (preferences.simplifiedNavigation) {
      root.classList.add('simplified-navigation');
    } else {
      root.classList.remove('simplified-navigation');
    }

    // Colorblind theme
    if (preferences.colorblindTheme !== 'none') {
      themeService.setThemePreset(preferences.colorblindTheme as any);
    }
  }, [preferences]);

  const loadPreferences = async () => {
    setLoading(true);
    try {
      const prefs = await getAccessibilityPreferences();
      setPreferences(prefs);
    } catch (error) {
      logger.error('Error loading accessibility preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (key: keyof AccessibilityPreferences, value: boolean) => {
    // Optimistically update UI
    setPreferences(prev => ({ ...prev, [key]: value }));

    // Update in database
    const result = await updateAccessibilityPreferences({ [key]: value });
    if (!result.success) {
      // Revert on error
      setPreferences(prev => ({ ...prev, [key]: !value }));
      logger.error('Failed to update accessibility preference');
    }
  };

  const refreshPreferences = async () => {
    await loadPreferences();
  };

  return (
    <AccessibilityContext.Provider
      value={{
        preferences,
        loading,
        updatePreference,
        refreshPreferences,
      }}
    >
      {children}
    </AccessibilityContext.Provider>
  );
};
